/**
 * Mobile-side error reporting.
 *
 * Routes exceptions through the PostHog client owned by the analytics
 * adapter (`./analytics`) — the same connection product analytics uses,
 * so there is no second SDK and no separate consent gate. See ADR-0065
 * (and ADR-0063 for the adapter pattern).
 *
 * Both surfaces always emit a structured `[mobile-error]` / `[mobile-warn]`
 * console line (the floor we never lose) AND call PostHog's
 * `captureException` when the analytics adapter is opted in. Error
 * capture follows the `analytics` consent purpose (ADR-0065): when the
 * user has not consented to analytics, the adapter's opt-out gate drops
 * the exception before it reaches the SDK, exactly like `track()`.
 *
 * What's wired up:
 *   - `ErrorBoundary` calls `reportError` when the React tree throws.
 *   - The tRPC fetch wrapper reports terminal failures (network drops,
 *     5xx responses, refresh failures).
 *   - PostHog Error Tracking autocapture (enabled in the adapter's
 *     `init`, see `posthog-analytics.ts`) installs the RN global handlers
 *     so uncaught JS errors and unhandled promise rejections OUTSIDE the
 *     two paths above (event handlers, effects, fire-and-forget promises)
 *     are captured too.
 *   - Native iOS/Android crashes (native-module faults, OOM, startup
 *     crashes, ANRs) are captured via `@posthog/react-native-plugin`
 *     (`errorTracking.autocapture.nativeCrashes`). The plugin is an
 *     autolinked native module, so this only activates on a real
 *     dev/prod build — it's a no-op on web, in Expo Go, and in the test
 *     harness. Readable native stack traces require uploading debug
 *     symbols at EAS build time (see docs/deployment.md).
 *
 * History: previously a dedicated `@sentry/react-native` integration with
 * its own `errorReports` consent purpose. Folded into the PostHog pipeline
 * per ADR-0065 — mirrors the API's move to PostHog Error Tracking in
 * `apps/api/src/lib/observability.ts`.
 */

import { captureException } from './analytics';

/**
 * Retained as a no-op seam so `_layout.tsx` can call it next to
 * `initAnalytics()` without knowing the PostHog client lifecycle is
 * owned by the analytics adapter. Mirrors the API's `initObservability`.
 */
export function initObservability(): void {
  // PostHog client lifecycle is owned by `analytics.initAnalytics`.
}

export function reportError(err: unknown, context?: Record<string, unknown> | undefined): void {
  // Console-log unconditionally — local diagnostics work without consent.
  console.error('[mobile-error]', err, context ?? '');
  captureException(err, context);
}

export function reportWarning(message: string, context?: Record<string, unknown> | undefined): void {
  console.warn('[mobile-warn]', message, context ?? '');
  // Wrap the message in an Error so PostHog Error Tracking gets a stack
  // pointing at the call site (useful for grouping in the UI).
  // `$exception_level: 'warning'` flags it as non-paging. Mirrors the
  // API's `reportWarning` shape.
  captureException(new Error(message), { ...context, $exception_level: 'warning' });
}
